import { useState } from 'react';
import { motion } from 'framer-motion';
import { Users, HandHeart, Building2, MapPin, Calendar, Check, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WalkingTours = () => {
  const [activeTab, setActiveTab] = useState('book');
  const [loading, setLoading] = useState(false);

  const [attendeeForm, setAttendeeForm] = useState({
    name: '',
    email: '',
    phone: '',
    guests: 1,
    tour_date: ''
  });

  const [volunteerForm, setVolunteerForm] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    availability: ''
  });

  const [sponsorForm, setSponsorForm] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: '',
    sponsorship_interest: '',
    message: ''
  });

  const handleAttendeeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/walking-tours/attendee`, attendeeForm);
      toast.success(response.data.message);
      setAttendeeForm({ name: '', email: '', phone: '', guests: 1, tour_date: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVolunteerSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/walking-tours/volunteer`, volunteerForm);
      toast.success(response.data.message);
      setVolunteerForm({ name: '', email: '', phone: '', experience: '', availability: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSponsorSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/walking-tours/sponsor`, sponsorForm);
      toast.success(response.data.message);
      setSponsorForm({ business_name: '', contact_name: '', email: '', phone: '', sponsorship_interest: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const benefits = [
    'Discover hidden gems and local history',
    'Meet fellow travelers and locals',
    'Family-friendly routes and storytelling',
    'Support local businesses along the way',
    'Learn insider tips about Penticton life'
  ];

  return (
    <div className="w-full" data-testid="walking-tours-page">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_fifa-fan-guides/artifacts/iytrv2ee_stenberg-print-0982.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        </div>

        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <motion.div {...fadeInUp}>
            <h1 className="hero-title text-5xl sm:text-6xl lg:text-7xl text-white mb-6" data-testid="page-title">
              Walking Tours of Penticton
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              Experience Penticton's stories, culture, and community on foot this summer
            </p>
          </motion.div>
        </div>
      </section>

      {/* About the Tours */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="about-tours-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl sm:text-5xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Explore Penticton Like a Local
            </h2>
            <p className="text-lg text-[#4A4A4A] mb-6 leading-relaxed">
              Our guided walking tours are more than just sightseeing—they're storytelling experiences that connect you with Penticton's heart and soul. From historic landmarks to modern hidden gems, discover the places that make this community special.
            </p>
            <p className="text-lg text-[#4A4A4A] leading-relaxed">
              Each tour is designed to be engaging for families, informative for visitors, and surprising even for long-time residents.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-[#F5E9D4] p-8 rounded-2xl grain-texture">
              <h3 className="text-2xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                What Makes Our Tours Special
              </h3>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="w-5 h-5 text-[#2E8B57] mr-3 mt-1 flex-shrink-0" />
                    <span className="text-[#1A1A1A]">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Upcoming Events - Eventbrite */}
      <section className="bg-[#F5E9D4] grain-texture py-20" data-testid="upcoming-events-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Attend a Tour
            </h2>
            <p className="text-[#4A4A4A] text-lg mb-8">
              Check out our upcoming walking tours and reserve your spot
            </p>
          </motion.div>

          {/* Upcoming Dates */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-white p-8 rounded-2xl mb-8"
          >
            <h3 className="text-2xl mb-6 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
              Upcoming Tour Dates
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-[#F5E9D4] rounded-xl">
                <Calendar className="w-8 h-8 text-[#1E6FBA] mx-auto mb-3" />
                <p className="text-2xl font-bold text-[#1E6FBA] mb-2">May 17</p>
                <p className="text-[#4A4A4A] font-medium">Saturday, 2026</p>
                <p className="text-sm text-[#757575] mt-2">Starting at SS Sicamous</p>
              </div>
              <div className="text-center p-6 bg-[#F5E9D4] rounded-xl">
                <Calendar className="w-8 h-8 text-[#1E6FBA] mx-auto mb-3" />
                <p className="text-2xl font-bold text-[#1E6FBA] mb-2">May 23</p>
                <p className="text-[#4A4A4A] font-medium">Saturday, 2026</p>
                <p className="text-sm text-[#757575] mt-2">Starting at SS Sicamous</p>
              </div>
              <div className="text-center p-6 bg-[#F5E9D4] rounded-xl">
                <Calendar className="w-8 h-8 text-[#1E6FBA] mx-auto mb-3" />
                <p className="text-2xl font-bold text-[#1E6FBA] mb-2">June 7</p>
                <p className="text-[#4A4A4A] font-medium">Sunday, 2026</p>
                <p className="text-sm text-[#757575] mt-2">Starting at SS Sicamous</p>
              </div>
            </div>
          </motion.div>

          {/* Eventbrite Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <a
              href="https://www.eventbrite.com/o/113831756311"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                className="btn-scale bg-[#1E6FBA] hover:bg-[#1558A0] text-white rounded-full px-8 py-6 text-lg"
                data-testid="eventbrite-button"
              >
                View All Tours & Book on Eventbrite
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </a>
            <p className="text-[#757575] text-sm mt-4">
              Pay-what-you-can pricing • 2-hour tours • All ages welcome
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sign Up Tabs */}
      <section className="bg-white py-20" data-testid="signup-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Get Involved
            </h2>
            <p className="text-[#4A4A4A] text-lg mb-8 max-w-2xl mx-auto">
              Whether you want to book, volunteer, or sponsor—we'd love to have you join us
            </p>
          </motion.div>

          {/* Tabs */}
          <div className="flex justify-center mb-8 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('book')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'book'
                  ? 'text-[#1E6FBA] border-b-2 border-[#1E6FBA]'
                  : 'text-[#757575] hover:text-[#1E6FBA]'
              }`}
              data-testid="tab-book"
            >
              <Users className="w-5 h-5 inline mr-2" />
              Book a Tour
            </button>
            <button
              onClick={() => setActiveTab('volunteer')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'volunteer'
                  ? 'text-[#1E6FBA] border-b-2 border-[#1E6FBA]'
                  : 'text-[#757575] hover:text-[#1E6FBA]'
              }`}
              data-testid="tab-volunteer"
            >
              <HandHeart className="w-5 h-5 inline mr-2" />
              Volunteer as Guide
            </button>
            <button
              onClick={() => setActiveTab('sponsor')}
              className={`px-6 py-3 font-medium transition-colors ${
                activeTab === 'sponsor'
                  ? 'text-[#1E6FBA] border-b-2 border-[#1E6FBA]'
                  : 'text-[#757575] hover:text-[#1E6FBA]'
              }`}
              data-testid="tab-sponsor"
            >
              <Building2 className="w-5 h-5 inline mr-2" />
              Become a Sponsor
            </button>
          </div>

          {/* Book a Tour Form */}
          {activeTab === 'book' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-[#F5E9D4] p-8 rounded-2xl"
              data-testid="book-form"
            >
              <h3 className="text-2xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Book Your Private Walking Tour
              </h3>
              <form onSubmit={handleAttendeeSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="attendee-name">Your Name</Label>
                  <Input
                    id="attendee-name"
                    type="text"
                    required
                    value={attendeeForm.name}
                    onChange={(e) => setAttendeeForm({ ...attendeeForm, name: e.target.value })}
                    data-testid="attendee-name-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="attendee-email">Email</Label>
                    <Input
                      id="attendee-email"
                      type="email"
                      required
                      value={attendeeForm.email}
                      onChange={(e) => setAttendeeForm({ ...attendeeForm, email: e.target.value })}
                      data-testid="attendee-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="attendee-phone">Phone</Label>
                    <Input
                      id="attendee-phone"
                      type="tel"
                      required
                      value={attendeeForm.phone}
                      onChange={(e) => setAttendeeForm({ ...attendeeForm, phone: e.target.value })}
                      data-testid="attendee-phone-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="attendee-guests">Number of Guests</Label>
                    <Input
                      id="attendee-guests"
                      type="number"
                      min="1"
                      required
                      value={attendeeForm.guests}
                      onChange={(e) => setAttendeeForm({ ...attendeeForm, guests: parseInt(e.target.value) })}
                      data-testid="attendee-guests-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="attendee-date">Preferred Tour Date</Label>
                    <Input
                      id="attendee-date"
                      type="date"
                      required
                      value={attendeeForm.tour_date}
                      onChange={(e) => setAttendeeForm({ ...attendeeForm, tour_date: e.target.value })}
                      data-testid="attendee-date-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full btn-scale bg-[#1E6FBA] hover:bg-[#1558A0] text-white py-6"
                  disabled={loading}
                  data-testid="attendee-submit-button"
                >
                  {loading ? 'Submitting...' : 'Sign Me Up'}
                </Button>
              </form>
            </motion.div>
          )}

          {/* Volunteer Form */}
          {activeTab === 'volunteer' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-[#F5E9D4] p-8 rounded-2xl"
              data-testid="volunteer-form"
            >
              <h3 className="text-2xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Volunteer as a Tour Guide
              </h3>
              <p className="text-[#4A4A4A] mb-6">
                Share your love of Penticton by guiding visitors and locals through our community's best spots.
              </p>
              <form onSubmit={handleVolunteerSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="volunteer-name">Your Name</Label>
                  <Input
                    id="volunteer-name"
                    type="text"
                    required
                    value={volunteerForm.name}
                    onChange={(e) => setVolunteerForm({ ...volunteerForm, name: e.target.value })}
                    data-testid="volunteer-name-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="volunteer-email">Email</Label>
                    <Input
                      id="volunteer-email"
                      type="email"
                      required
                      value={volunteerForm.email}
                      onChange={(e) => setVolunteerForm({ ...volunteerForm, email: e.target.value })}
                      data-testid="volunteer-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="volunteer-phone">Phone</Label>
                    <Input
                      id="volunteer-phone"
                      type="tel"
                      required
                      value={volunteerForm.phone}
                      onChange={(e) => setVolunteerForm({ ...volunteerForm, phone: e.target.value })}
                      data-testid="volunteer-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="volunteer-experience">Relevant Experience</Label>
                  <Textarea
                    id="volunteer-experience"
                    required
                    value={volunteerForm.experience}
                    onChange={(e) => setVolunteerForm({ ...volunteerForm, experience: e.target.value })}
                    placeholder="Tell us about your connection to Penticton or tour guiding experience"
                    data-testid="volunteer-experience-input"
                  />
                </div>
                <div>
                  <Label htmlFor="volunteer-availability">Availability</Label>
                  <Input
                    id="volunteer-availability"
                    type="text"
                    required
                    value={volunteerForm.availability}
                    onChange={(e) => setVolunteerForm({ ...volunteerForm, availability: e.target.value })}
                    placeholder="e.g., Weekends in July, Tuesday mornings"
                    data-testid="volunteer-availability-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-scale bg-[#2E8B57] hover:bg-[#26734B] text-white py-6"
                  disabled={loading}
                  data-testid="volunteer-submit-button"
                >
                  {loading ? 'Submitting...' : 'Apply to Volunteer'}
                </Button>
              </form>
            </motion.div>
          )}

          {/* Sponsor Form */}
          {activeTab === 'sponsor' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-[#F5E9D4] p-8 rounded-2xl"
              data-testid="sponsor-form"
            >
              <h3 className="text-2xl mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Become a Tour Sponsor
              </h3>
              <p className="text-[#4A4A4A] mb-4">
                Partner with us to showcase your business to tour participants. Sponsorship benefits include:
              </p>
              <ul className="mb-6 space-y-2">
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#2E8B57] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-[#4A4A4A]">Your business featured as a tour stop</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#2E8B57] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-[#4A4A4A]">Brand placement on tour materials</span>
                </li>
                <li className="flex items-start">
                  <Check className="w-5 h-5 text-[#2E8B57] mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-[#4A4A4A]">Shoutouts during tours and on social media</span>
                </li>
              </ul>
              <form onSubmit={handleSponsorSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="sponsor-business">Business Name</Label>
                  <Input
                    id="sponsor-business"
                    type="text"
                    required
                    value={sponsorForm.business_name}
                    onChange={(e) => setSponsorForm({ ...sponsorForm, business_name: e.target.value })}
                    data-testid="sponsor-business-input"
                  />
                </div>
                <div>
                  <Label htmlFor="sponsor-contact">Contact Name</Label>
                  <Input
                    id="sponsor-contact"
                    type="text"
                    required
                    value={sponsorForm.contact_name}
                    onChange={(e) => setSponsorForm({ ...sponsorForm, contact_name: e.target.value })}
                    data-testid="sponsor-contact-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sponsor-email">Email</Label>
                    <Input
                      id="sponsor-email"
                      type="email"
                      required
                      value={sponsorForm.email}
                      onChange={(e) => setSponsorForm({ ...sponsorForm, email: e.target.value })}
                      data-testid="sponsor-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sponsor-phone">Phone</Label>
                    <Input
                      id="sponsor-phone"
                      type="tel"
                      required
                      value={sponsorForm.phone}
                      onChange={(e) => setSponsorForm({ ...sponsorForm, phone: e.target.value })}
                      data-testid="sponsor-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="sponsor-interest">Sponsorship Interest</Label>
                  <Input
                    id="sponsor-interest"
                    type="text"
                    required
                    value={sponsorForm.sponsorship_interest}
                    onChange={(e) => setSponsorForm({ ...sponsorForm, sponsorship_interest: e.target.value })}
                    placeholder="e.g., Tour stop, Material branding, Both"
                    data-testid="sponsor-interest-input"
                  />
                </div>
                <div>
                  <Label htmlFor="sponsor-message">Additional Information (Optional)</Label>
                  <Textarea
                    id="sponsor-message"
                    value={sponsorForm.message}
                    onChange={(e) => setSponsorForm({ ...sponsorForm, message: e.target.value })}
                    placeholder="Tell us more about your business and sponsorship goals"
                    data-testid="sponsor-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-scale bg-[#F28A30] hover:bg-[#D97725] text-white py-6"
                  disabled={loading}
                  data-testid="sponsor-submit-button"
                >
                  {loading ? 'Submitting...' : 'Submit Sponsorship Inquiry'}
                </Button>
              </form>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default WalkingTours;
